import { format } from "date-fns";
import { RacePlan } from "./dategrid";
import { getWeekDistance, render, renderDist } from "./rendering";
import { Units } from "types/app";

/**
 * Converts a training plan to Strava-compatible format
 * Strava accepts structured workout data in a specific text format
 * This creates a formatted text file that can be manually entered into Strava
 */
export function toStrava(plan: RacePlan, units: Units): string | undefined {
  const lines: string[] = [];

  // Header
  lines.push(`STRAVA TRAINING PLAN`);
  lines.push(`${plan.raceType} Training Plan`);
  lines.push(`Generated by ProgressPlans`);
  lines.push(
    `Race Date: ${format(plan.planDates.planEndDate, "MMMM d, yyyy")}`,
  );
  lines.push(`Units: ${units}`);
  lines.push(`\n${"=".repeat(60)}\n`);

  const weeks = plan.dateGrid.weeks;

  for (let i = 0; i < weeks.length; i++) {
    const currWeek = weeks[i];
    const distance = getWeekDistance(currWeek, units);

    // Week header
    if (i === weeks.length - 1) {
      lines.push(`WEEK ${i + 1} - RACE WEEK 🏁`);
    } else {
      lines.push(`WEEK ${i + 1}`);
    }
    lines.push(
      `Dates: ${format(currWeek.days[0].date, "MMM d")} - ${format(currWeek.days[6].date, "MMM d, yyyy")}`,
    );

    if (distance[0] > 0) {
      lines.push(`Weekly Distance: ${renderDist(distance, units, units)}`);
    }
    lines.push("");

    // Daily workouts
    for (let j = 0; j < currWeek.days.length; j++) {
      const currWorkout = currWeek.days[j];
      const dayName = format(currWorkout.date, "EEEE");
      const dateStr = format(currWorkout.date, "MMM d");

      if (currWorkout.event) {
        const [title, desc] = render(
          currWorkout.event,
          plan.sourceUnits,
          units,
        );
        const workoutType = currWorkout.event.type || "Run";

        // Parse distance
        let distanceStr = "";
        if (currWorkout.event.dist !== undefined) {
          let distance = 0;
          if (typeof currWorkout.event.dist === "number") {
            distance = currWorkout.event.dist;
          } else if (
            Array.isArray(currWorkout.event.dist) &&
            currWorkout.event.dist.length > 0
          ) {
            distance = currWorkout.event.dist[0];
          }
          distanceStr = ` - ${distance.toFixed(1)} ${units}`;
        }

        lines.push(`  ${dayName}, ${dateStr}:`);
        lines.push(`    Type: ${workoutType}`);
        lines.push(`    Workout: ${title}${distanceStr}`);

        if (desc && desc.trim() !== "") {
          const descLines = desc
            .split("\n")
            .filter((line) => line.trim() !== "");
          lines.push(`    Details:`);
          descLines.forEach((descLine) => {
            lines.push(`      - ${descLine.trim()}`);
          });
        }
        lines.push("");
      } else {
        lines.push(`  ${dayName}, ${dateStr}: Rest Day`);
        lines.push("");
      }
    }

    lines.push(`${"-".repeat(60)}\n`);
  }

  // Summary
  lines.push(`TRAINING PLAN SUMMARY`);
  lines.push(`${"=".repeat(60)}`);
  lines.push(`Total Weeks: ${weeks.length}`);

  // Calculate totals
  let totalDistance = 0;
  let totalWorkouts = 0;
  weeks.forEach((week) => {
    const distance = getWeekDistance(week, units);
    totalDistance += distance[0];
    week.days.forEach((day) => {
      if (day.event) totalWorkouts++;
    });
  });

  lines.push(`Total Distance: ${Math.round(totalDistance)} ${units}`);
  lines.push(`Total Workouts: ${totalWorkouts}`);
  lines.push(
    `Race Day: ${format(plan.planDates.planEndDate, "EEEE, MMMM d, yyyy")}`,
  );
  lines.push(`\nImport Instructions:`);
  lines.push(`1. Open this file in a text editor`);
  lines.push(`2. Copy individual workout descriptions`);
  lines.push(`3. In Strava, go to Training > My Training Plans`);
  lines.push(`4. Create a new training plan and add workouts manually`);
  lines.push(
    `5. Or use the Strava Calendar to schedule runs with these details`,
  );

  return lines.join("\n");
}
